import { useEffect, useRef, useState } from 'react'
import {
    acquireCameraStream,
    stopCameraStream,
    effectiveAspect,
} from '../../WebAi/vision/cameraConfig'

function ROIModule({ roiRegions, setRoiRegions, activeRegionId }) {
    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const streamRef = useRef(null)
    const [isResizing, setIsResizing] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [isCameraReady, setIsCameraReady] = useState(false)
    const [isDraggingLabel, setIsDraggingLabel] = useState(false)
    const [labelDragOffset, setLabelDragOffset] = useState({ x: 0, y: 0 })

    const [draggingCornerIndex, setDraggingCornerIndex] = useState(null)
    // Match the actual camera feed's shape (orientation-aware) so the drawing
    // area is the SAME ratio the Execute page shows — a portrait phone reads
    // 9:16 here too, not a fixed 16:9 (which was confusing). Regions are stored
    // as percentages, so the absolute size only sets the drawing scale.
    const CAM_ASPECT = effectiveAspect() // width / height (>=1 landscape)
    const CAM_WIDTH = CAM_ASPECT >= 1 ? 900 : Math.round(900 * CAM_ASPECT)
    const CAM_HEIGHT = CAM_ASPECT >= 1 ? Math.round(900 / CAM_ASPECT) : 900

    useEffect(() => {
        startCamera()

        return () => {
            stopCameraStream(streamRef.current)
        }
    }, [])

    const startCamera = async () => {
        try {
            // Honour the AI Studio camera choice (incl. a paired phone camera).
            const deviceId =
                sessionStorage.getItem('aiCameraDeviceId') || undefined
            const stream = await acquireCameraStream(deviceId)

            streamRef.current = stream
            videoRef.current.srcObject = stream

            videoRef.current.onloadeddata = () => {
                setIsCameraReady(true)
            }

            videoRef.current.play()
        } catch (err) {
            console.error('Camera permission denied', err)
        }
    }

    const getLabelSize = (region) => {
        const ctx = canvasRef.current.getContext('2d')
        ctx.font = 'bold 14px Arial'

        const text = region.name || 'Region'
        const paddingX = 10
        const tagHeight = 24
        const textWidth = ctx.measureText(text).width

        return {
            tagWidth: textWidth + paddingX * 2,
            tagHeight,
        }
    }

    // Drag active ROI only
    const handleMouseMove = (e) => {
        if (!activeRegionId) return

        const rect = canvasRef.current.getBoundingClientRect()
        // Map pointer to the canvas' internal resolution so the drawing stays
        // correct when the canvas is CSS-scaled to fit its container.
        const scaleX = CAM_WIDTH / rect.width
        const scaleY = CAM_HEIGHT / rect.height
        const mouseX = (e.clientX - rect.left) * scaleX
        const mouseY = (e.clientY - rect.top) * scaleY

        // ===== LABEL DRAGGING =====
        if (isDraggingLabel) {
            const updated = roiRegions.map((region) => {
                if (region.id !== activeRegionId) return region

                const x = region.xPercent * CAM_WIDTH
                const y = region.yPercent * CAM_HEIGHT
                const width = region.widthPercent * CAM_WIDTH
                const height = region.heightPercent * CAM_HEIGHT

                const newLabelX = mouseX - labelDragOffset.x
                const newLabelY = mouseY - labelDragOffset.y

                // Clamp inside rectangle
                const { tagWidth, tagHeight } = getLabelSize(region)

                const clampedX = Math.max(
                    x,
                    Math.min(newLabelX, x + width - tagWidth)
                )

                const clampedY = Math.max(
                    y,
                    Math.min(newLabelY, y + height - tagHeight)
                )

                return {
                    ...region,
                    labelOffsetX: clampedX - x,
                    labelOffsetY: clampedY - y,
                }
            })

            setRoiRegions(updated)
            return
        }

        // =========================
        // CORNER DRAG (GROUND MODE)
        // =========================
        if (draggingCornerIndex !== null) {
            const updated = roiRegions.map((region) => {
                if (region.id !== activeRegionId) return region

                const newPoints = [...region.points]

                newPoints[draggingCornerIndex] = {
                    xPercent: Math.min(1, Math.max(0, mouseX / CAM_WIDTH)),
                    yPercent: Math.min(1, Math.max(0, mouseY / CAM_HEIGHT)),
                }

                return {
                    ...region,
                    points: newPoints,
                }
            })

            setRoiRegions(updated)
            return
        }

        // If not resizing or dragging rectangle, exit
        if (!isDragging && !isResizing) return

        // =========================
        // RECTANGLE MODE DRAG
        // =========================
        const updated = roiRegions.map((region) => {
            if (region.id !== activeRegionId) return region

            const x = region.xPercent * CAM_WIDTH
            const y = region.yPercent * CAM_HEIGHT
            const width = region.widthPercent * CAM_WIDTH
            const height = region.heightPercent * CAM_HEIGHT

            if (isResizing) {
                const newWidth = Math.min(
                    CAM_WIDTH - x,
                    Math.max(30, mouseX - x)
                )

                const newHeight = Math.min(
                    CAM_HEIGHT - y,
                    Math.max(30, mouseY - y)
                )

                return {
                    ...region,
                    widthPercent: newWidth / CAM_WIDTH,
                    heightPercent: newHeight / CAM_HEIGHT,
                }
            }

            const newX = Math.min(
                CAM_WIDTH - width,
                Math.max(0, mouseX - width / 2)
            )

            const newY = Math.min(
                CAM_HEIGHT - height,
                Math.max(0, mouseY - height / 2)
            )

            return {
                ...region,
                xPercent: newX / CAM_WIDTH,
                yPercent: newY / CAM_HEIGHT,
            }
        })

        setRoiRegions(updated)
    }

    const handleMouseDown = (e) => {
        if (!activeRegionId) return

        const activeRegion = roiRegions.find((r) => r.id === activeRegionId)
        if (!activeRegion) return

        const rect = canvasRef.current.getBoundingClientRect()
        // Map pointer to the canvas' internal resolution so the drawing stays
        // correct when the canvas is CSS-scaled to fit its container.
        const scaleX = CAM_WIDTH / rect.width
        const scaleY = CAM_HEIGHT / rect.height
        const mouseX = (e.clientX - rect.left) * scaleX
        const mouseY = (e.clientY - rect.top) * scaleY

        // ✅ FIRST define region box values
        const x = activeRegion.xPercent * CAM_WIDTH
        const y = activeRegion.yPercent * CAM_HEIGHT
        const width = activeRegion.widthPercent * CAM_WIDTH
        const height = activeRegion.heightPercent * CAM_HEIGHT

        // ===== LABEL DRAG =====
        const text = activeRegion.name || 'Region'
        const ctx = canvasRef.current.getContext('2d')
        ctx.font = 'bold 14px Arial'

        const textWidth = ctx.measureText(text).width
        const paddingX = 10
        const tagHeight = 24
        const tagWidth = textWidth + paddingX * 2

        const labelX = x + (activeRegion.labelOffsetX || 10)
        const labelY = y + (activeRegion.labelOffsetY || -30)

        if (
            mouseX >= labelX &&
            mouseX <= labelX + tagWidth &&
            mouseY >= labelY &&
            mouseY <= labelY + tagHeight
        ) {
            setIsDraggingLabel(true)
            setLabelDragOffset({
                x: mouseX - labelX,
                y: mouseY - labelY,
            })
            return
        }

        // ===== GROUND MODE CORNER DRAG =====
        if (
            activeRegion.mode === 'ground' &&
            activeRegion.points?.length === 4
        ) {
            const pts = activeRegion.points.map((p) => ({
                x: p.xPercent * CAM_WIDTH,
                y: p.yPercent * CAM_HEIGHT,
            }))

            const hitRadius = 12

            for (let i = 0; i < pts.length; i++) {
                const dx = mouseX - pts[i].x
                const dy = mouseY - pts[i].y
                const distance = Math.sqrt(dx * dx + dy * dy)

                if (distance < hitRadius) {
                    setDraggingCornerIndex(i)
                    return
                }
            }
        }

        // ===== RECTANGLE MODE =====
        const cornerSize = 15

        if (
            mouseX > x + width - cornerSize &&
            mouseY > y + height - cornerSize
        ) {
            setIsResizing(true)
        } else {
            setIsDragging(true)
        }
    }

    const handleMouseUp = () => {
        if (activeRegionId) {
            const updated = roiRegions.map((region) => {
                if (region.id !== activeRegionId) return region
                if (!region.points || region.points.length !== 4) return region

                const center = {
                    x:
                        region.points.reduce((sum, p) => sum + p.xPercent, 0) /
                        4,
                    y:
                        region.points.reduce((sum, p) => sum + p.yPercent, 0) /
                        4,
                }

                const sorted = [...region.points].sort((a, b) => {
                    const angleA = Math.atan2(
                        a.yPercent - center.y,
                        a.xPercent - center.x
                    )
                    const angleB = Math.atan2(
                        b.yPercent - center.y,
                        b.xPercent - center.x
                    )
                    return angleA - angleB
                })

                return { ...region, points: sorted }
            })

            setRoiRegions(updated)
        }

        setIsDraggingLabel(false)
        setIsResizing(false)
        setIsDragging(false)
        setDraggingCornerIndex(null)
    }

    useEffect(() => {
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        let animationId

        const draw = () => {
            ctx.clearRect(0, 0, CAM_WIDTH, CAM_HEIGHT)

            roiRegions.forEach((region) => {
                const isActive = region.id === activeRegionId

                const x = region.xPercent * CAM_WIDTH
                const y = region.yPercent * CAM_HEIGHT
                const width = region.widthPercent * CAM_WIDTH
                const height = region.heightPercent * CAM_HEIGHT

                // ===== Draw Border =====
                ctx.strokeStyle = isActive ? '#FF3B3B' : '#2ECC71'
                ctx.lineWidth = 3
                if (region.mode !== 'ground') {
                    ctx.strokeRect(x, y, width, height)
                } else if (region.points?.length === 4) {
                    const pts = region.points.map((p) => ({
                        x: p.xPercent * CAM_WIDTH,
                        y: p.yPercent * CAM_HEIGHT,
                    }))

                    ctx.beginPath()
                    ctx.moveTo(pts[0].x, pts[0].y)
                    ctx.lineTo(pts[1].x, pts[1].y)
                    ctx.lineTo(pts[2].x, pts[2].y)
                    ctx.lineTo(pts[3].x, pts[3].y)
                    ctx.closePath()
                    ctx.stroke()

                    // draw corner handles
                    pts.forEach((p) => {
                        ctx.fillStyle = '#FF3B3B'
                        ctx.fillRect(p.x - 6, p.y - 6, 12, 12)
                    })
                }

                // ===== Draw Region Name Tag =====
                const text = region.name || 'Region'

                ctx.font = 'bold 14px Arial'
                ctx.textBaseline = 'middle'

                const textWidth = ctx.measureText(text).width
                const paddingX = 10
                const tagHeight = 24
                const tagWidth = textWidth + paddingX * 2

                const labelX = x + (region.labelOffsetX || 10)
                const labelY = y + (region.labelOffsetY || -30)

                // Tag background
                ctx.fillStyle = isActive
                    ? 'rgba(255, 59, 59, 0.95)'
                    : 'rgba(46, 204, 113, 0.95)'

                const radius = 6
                ctx.beginPath()
                ctx.moveTo(labelX + radius, labelY)
                ctx.lineTo(labelX + tagWidth - radius, labelY)
                ctx.quadraticCurveTo(
                    labelX + tagWidth,
                    labelY,
                    labelX + tagWidth,
                    labelY + radius
                )
                ctx.lineTo(labelX + tagWidth, labelY + tagHeight - radius)
                ctx.quadraticCurveTo(
                    labelX + tagWidth,
                    labelY + tagHeight,
                    labelX + tagWidth - radius,
                    labelY + tagHeight
                )
                ctx.lineTo(labelX + radius, labelY + tagHeight)
                ctx.quadraticCurveTo(
                    labelX,
                    labelY + tagHeight,
                    labelX,
                    labelY + tagHeight - radius
                )
                ctx.lineTo(labelX, labelY + radius)
                ctx.quadraticCurveTo(labelX, labelY, labelX + radius, labelY)
                ctx.closePath()
                ctx.fill()

                // Text
                ctx.fillStyle = '#fff'
                ctx.fillText(text, labelX + paddingX, labelY + tagHeight / 2)

                // ===== Resize Handle (Active Only) =====
                if (isActive) {
                    ctx.fillStyle = '#FF3B3B'
                    ctx.fillRect(x + width - 12, y + height - 12, 12, 12)
                }
            })

            animationId = requestAnimationFrame(draw)
        }

        draw()
        return () => cancelAnimationFrame(animationId)
    }, [roiRegions, activeRegionId])

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                maxWidth: CAM_WIDTH,
                aspectRatio: `${CAM_WIDTH} / ${CAM_HEIGHT}`,
                borderRadius: 28,
                overflow: 'hidden',
                background: '#e6e6e6',
                boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
            }}
        >
            {/* ===== Placeholder (Teachable Machine Style) ===== */}
            {!isCameraReady && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(145deg, #eeeeee, #dddddd)',
                    }}
                >
                    <div
                        style={{
                            width: 90,
                            height: 90,
                            borderRadius: '50%',
                            background:
                                'radial-gradient(circle at 30% 30%, #dcdcdc, #bfbfbf)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow:
                                'inset 0 4px 10px rgba(255,255,255,0.6), 0 8px 20px rgba(0,0,0,0.08)',
                        }}
                    >
                        <span style={{ fontSize: 36 }}>📷</span>
                    </div>

                    <div
                        style={{
                            marginTop: 18,
                            fontSize: 16,
                            color: '#555',
                            fontWeight: 500,
                        }}
                    >
                        Initializing camera...
                    </div>
                </div>
            )}

            {/* ===== Video ===== */}
            <video
                ref={videoRef}
                width={CAM_WIDTH}
                height={CAM_HEIGHT}
                style={{
                    width: '100%',
                    height: '100%',
                    // Show the whole frame at the feed's real ratio (no zoom/
                    // crop) so what you draw here matches the Execute view.
                    objectFit: 'contain',
                    display: isCameraReady ? 'block' : 'none',
                }}
            />

            {/* ===== ROI Canvas ===== */}
            <canvas
                ref={canvasRef}
                width={CAM_WIDTH}
                height={CAM_HEIGHT}
                onMouseMove={handleMouseMove}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    cursor:
                        draggingCornerIndex !== null
                            ? 'grabbing'
                            : isResizing
                              ? 'nwse-resize'
                              : 'move',
                }}
            />
        </div>
    )
}

export default ROIModule
